
import Vue from "vue"
import Component from "vue-class-component"
import {Inject} from "vue-property-decorator"
import {AxiosError} from "axios"
import {AuthenticationService, MfaResponse, MfaType} from "@/AuthenticationService"

/**
 * MfaSetup — Multi-factor authentication setup component.
 *
 * Supports both EmlOtp (Email OTP) and AtTotp (Authenticator TOTP) MFA types,
 * as well as disabling MFA entirely (when the user already has MFA configured).
 *
 * Usage flow (setup):
 *   1. User selects an MFA type (EmlOtp or AtTotp).
 *   2. Component calls authenticationService.mfaSetup({ mfaType })
 *      without a code to initiate.
 *      - EmlOtp:  Server sends a one-time code to the user's email address.
 *      - AtTotp:  Generates a TOTP secret + QR code (returned in response).
 *   3. User scans QR code (AtTotp) or checks email (EmlOtp),
 *      then enters the 6-digit code.
 *   4. Component calls authenticationService.mfaSetup({ mfaType, code })
 *      to verify & complete.
 *   5. On success the component emits "setup-complete".
 *
 * Usage flow (disable):
 *   1. User selects "Disable 2FA" (only shown when user.mfaType
 *      is currently set).
 *   2. Component calls authenticationService.mfaSetup({ mfaType: null })
 *      — no code needed.
 *   3. Server disables MFA immediately; component moves to "done"
 *      and emits "setup-complete".
 *
 * Emits:
 *   setup-complete — MFA registration was verified/disabled successfully.
 *   cancel         — User clicked Cancel / Back at any step.
 */
@Component
export default class MfaSetup extends Vue {
  @Inject()
  authenticationService: AuthenticationService

  user: User | null = null

  // ── UI state ──────────────────────────────────────────────────────────────
  /** Which step the user is on: "select" → "verify" → "done" */
  step: "select" | "verify" | "done" = "select"

  isLoading = false
  errorMessage = ""

  // ── Form data ─────────────────────────────────────────────────────────────
  /** null means "disable MFA" */
  selectedMfaType: MfaType | null = MfaType.EmlOtp

  /** 6-digit verification code entered by the user */
  verificationCode = ""

  // ── Response data from initiation call ────────────────────────────────────
  /** Instruction / status message returned by the server */
  serverMessage = ""

  /** Base64-encoded PNG QR code (AtTotp only) */
  qrCodeImage: string | null = null

  /** Plain-text TOTP secret for manual entry (AtTotp only) */
  secret: string | null = null

  async created() {
    this.user = await this.authenticationService.loadCurrentUser()
    this.selectedMfaType = this.user?.mfaType ?? MfaType.EmlOtp
  }

  // ── Computed helpers ──────────────────────────────────────────────────────
  get isMfaEnabled(): boolean {
    return this.user?.mfaType != null
  }

  get isAtTotp(): boolean {
    return this.selectedMfaType === MfaType.AtTotp
  }

  get isEmlOtp(): boolean {
    return this.selectedMfaType === MfaType.EmlOtp
  }

  get isDisable(): boolean {
    return this.selectedMfaType === null
  }

  get canSubmitCode(): boolean {
    return this.verificationCode.length === 6
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  /** Step 1 — Initiate setup for the chosen MFA type,
   * or disable MFA immediately. */
  async initiateSetup(): Promise<void> {
    this.errorMessage = ""
    this.isLoading = true

    try {
      const response: MfaResponse = await this.authenticationService.mfaSetup({
        mfaType: this.selectedMfaType,
      })

      this.serverMessage = response.message

      if (this.isDisable) {
        // Server requires a verification code to confirm disabling
        // — go to verify step.
        this.step = "verify"
      } else {
        this.qrCodeImage = response.qrCodeImage ?? null
        this.secret = response.secret ?? null
        this.step = "verify"
      }
    } catch (error) {
      const axiosError = <AxiosError<MfaResponse>>error
      const responseData: MfaResponse | undefined = axiosError.response?.data
      this.errorMessage = responseData?.message ?? axiosError.message
    } finally {
      this.isLoading = false
    }
  }

  /** Step 2 — Verify the 6-digit code and complete setup. */
  async verifyCode(): Promise<void> {
    if (!this.canSubmitCode) return

    this.errorMessage = ""
    this.isLoading = true

    try {
      const response: MfaResponse = await this.authenticationService.mfaSetup({
        mfaType: this.selectedMfaType,
        code: this.verificationCode,
      })

      this.serverMessage = response.message
      this.user = await this.authenticationService.loadCurrentUser()
      this.step = "done"
    } catch (error) {
      const axiosError = <AxiosError<MfaResponse>>error
      const responseData: MfaResponse | undefined = axiosError.response?.data
      this.errorMessage = responseData?.message ?? axiosError.message
    } finally {
      this.isLoading = false
    }
  }

  /** Reset back to the type-selection step. */
  backToSelect(): void {
    this.step = "select"
    this.verificationCode = ""
    this.errorMessage = ""
    this.serverMessage = ""
    this.qrCodeImage = null
    this.secret = null
  }

  cancel(): void {
    this.backToSelect()
    this.$emit("cancel")
  }

  complete(): void {
    this.backToSelect()
    this.$emit("setup-complete")
  }
}
